/**
 * Koinonia Farm Theme - Collection Filtering
 *
 * Drives the filter sidebar/drawer in main-collection-product-grid.liquid.
 * Every control here is a real <a href> or a real GET <form> first — this
 * file is progressive enhancement on top of markup that already works with
 * JS off. If a fetch fails for any reason, we fall back to a real
 * navigation rather than failing silently.
 *
 * How the swap works: a filter change, a pagination click, or a chip
 * removal all resolve to a single collection URL. We fetch that URL with
 * &section_id=main-collection-product-grid appended (Shopify's section
 * rendering API — returns just this section's HTML, no page chrome), then
 * copy two pieces out of the response into the live page:
 *   - #CollectionFilterGroups  (the filter panel's contents — counts change
 *     with every filter, since Shopify computes them against whatever else
 *     is currently active)
 *   - #CollectionFacetsWrapper (chips, result count, grid, pagination)
 * #CollectionResultAnnouncer is deliberately OUTSIDE both of those and is
 * never replaced, only its textContent is set — a live region some screen
 * readers won't announce if the element itself is destroyed and recreated
 * rather than mutated in place.
 */
document.addEventListener('DOMContentLoaded', function () {
  var form = document.getElementById('CollectionFiltersForm');
  var wrapper = document.getElementById('CollectionFacetsWrapper');
  if (!form || !wrapper) return; // collection.filters is empty — nothing to wire up

  var toggle = document.getElementById('CollectionFiltersToggle');
  var closeBtn = document.getElementById('CollectionFiltersClose');
  var overlay = document.getElementById('CollectionFiltersOverlay');
  var announcer = document.getElementById('CollectionResultAnnouncer');
  var groups = document.getElementById('CollectionFilterGroups');
  var body = document.body;

  // ---------- drawer open/close (mobile) ----------

  function openDrawer() {
    body.classList.add('collection-filters-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    body.style.overflow = 'hidden';
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    body.classList.remove('collection-filters-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    body.style.overflow = '';
    if (toggle) toggle.focus();
  }

  if (toggle) {
    toggle.addEventListener('click', function () {
      if (body.classList.contains('collection-filters-open')) {
        closeDrawer();
      } else {
        openDrawer();
      }
    });
  }
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  if (overlay) overlay.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (!body.classList.contains('collection-filters-open')) return;
    closeDrawer();
  });

  // Focus trap for the drawer. Queries focusable elements FRESH on every
  // Tab press (rather than once at open-time, like the mobile nav menu's
  // trapFocus in global.js) because #CollectionFilterGroups' contents
  // change out from under the drawer on every filter change while it's
  // open — a stale focusable-element list would let Tab escape into a
  // checkbox that no longer exists.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    if (!body.classList.contains('collection-filters-open')) return;
    var panel = document.getElementById('CollectionFiltersForm');
    if (!panel) return;
    var focusable = panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  });

  // ---------- fetch + swap ----------

  function sectionUrl(url) {
    var u = new URL(url, window.location.origin);
    // Must be the template instance id (template--<n>__main-collection-product-grid),
    // read from the section's own markup. The bare type name renders the section
    // with its schema DEFAULTS (16 per page), not this template's settings.
    var sectionEl = wrapper.closest('[data-section-id]');
    u.searchParams.set('section_id', sectionEl ? sectionEl.getAttribute('data-section-id') : 'main-collection-product-grid');

    return u.toString();
  }

  function announce(text) {
    if (announcer) announcer.textContent = text;
  }

  // Guards against overlapping requests landing out of order — e.g. two
  // filter changes fired close together (a double-fire on fast input, a
  // stray keyup while focus is moving, or just two legitimate quick clicks)
  // whose responses come back in a different order than they were sent.
  // Without this, an OLDER response arriving AFTER a newer one would win
  // and silently revert the shopper's most recent choice — applied filter
  // flashes in, then reverts, with nothing in the console to explain why.
  // requestId is the belt, AbortController is the suspenders: aborting the
  // previous fetch stops it from even finishing the round trip, and the id
  // check catches anything that slips past the abort (a response that was
  // already fully buffered, browsers that don't abort a fetch already at
  // the .then() stage, etc.).
  var activeRequestId = 0;
  var activeController = null;

  /**
   * Fetches `url`, swaps #CollectionFilterGroups and #CollectionFacetsWrapper
   * from the response, updates the address bar, and re-focuses appropriately.
   *
   * opts.focusName / opts.focusValue: if this change came from a single
   * checkbox, refocus the SAME checkbox (matched by name+value) in the
   * freshly rendered markup once the swap lands — losing focus the moment a
   * shopper checks a box is a 2.4.3/2.4.7 regression a full-page reload
   * would not have caused, so it's worth the extra lookup.
   * opts.moveFocusToTop: pagination clicks move focus to the results
   * region instead, since there's no single control to return to.
   */
  function applyUrl(url, opts) {
    opts = opts || {};
    var target = sectionUrl(url);

    if (activeController) activeController.abort();
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    activeController = controller;
    var requestId = ++activeRequestId;

    fetch(target, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Facet fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        // A newer applyUrl() call has since started — this response is
        // stale even if it technically completed. Never let it touch the
        // DOM; the newer request (or its own .catch) owns what happens next.
        if (requestId !== activeRequestId) return;

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGroups = doc.getElementById('CollectionFilterGroups');
        var newWrapper = doc.getElementById('CollectionFacetsWrapper');
        if (!newGroups || !newWrapper) {
          // Shape changed under us (e.g. filters disabled mid-session) —
          // don't half-apply, just navigate for real.
          window.location.href = url;
          return;
        }

        if (groups) groups.innerHTML = newGroups.innerHTML;
        wrapper.innerHTML = newWrapper.innerHTML;

        if (!opts.skipPushState) history.pushState({}, '', url);

        var countEl = wrapper.querySelector('.collection-result-count');
        announce(countEl ? countEl.textContent.trim() : '');

        if (opts.moveFocusToTop) {
          wrapper.focus();
        } else if (opts.focusName) {
          var selector =
            '[name="' + cssEscape(opts.focusName) + '"]' +
            (opts.focusValue != null ? '[value="' + cssEscape(opts.focusValue) + '"]' : '');
          var el = document.querySelector(selector);
          if (el) el.focus();
        }

        document.dispatchEvent(new CustomEvent('koinonia:collection-updated', { detail: { url: url } }));
      })
      .catch(function (err) {
        if (requestId !== activeRequestId) return; // stale error, e.g. our own abort() above — the newer request is what matters now
        if (err && err.name === 'AbortError') return; // superseded, not a real failure — nothing to fall back to
        // Network hiccup or anything else unexpected — fall back to a real
        // navigation rather than leaving the shopper stuck on stale results.
        window.location.href = url;
      });
  }

  function cssEscape(value) {
    return window.CSS && CSS.escape ? CSS.escape(value) : value.replace(/(["\\])/g, '\\$1');
  }

  function buildUrlFromForm() {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (value === '') return; // empty price inputs etc. — don't clutter the URL with e.g. filter.v.price.gte=
      params.append(key, value);
    });
    var qs = params.toString();
    return form.action + (qs ? '?' + qs : '');
  }

  // ---------- checkbox auto-apply ----------

  form.addEventListener('change', function (e) {
    if (!e.target.matches('.filter-checkbox')) return;
    applyUrl(buildUrlFromForm(), { focusName: e.target.name, focusValue: e.target.value });
  });

  // ---------- sort select (form-associated via form="CollectionFiltersForm" in the Liquid, not a DOM descendant) ----------

  // FIX 2026-08-11: this used to be a SEPARATE handler in the section's own
  // inline <script> that did a real `window.location.href` navigation built
  // from `window.location.href` at click-time. That's a second, independent
  // path to changing the URL, racing this file's AJAX path — if a shopper's
  // sort change fired while a filter change was still in flight (pushState
  // not landed yet), the navigation read the STALE pre-filter URL and threw
  // the in-flight filter application away: applied, briefly visible, then
  // silently reverted. Routing sort through the SAME applyUrl()/
  // buildUrlFromForm() pipeline as checkboxes means there is exactly one
  // way this form ever gets submitted, always built from the form's live
  // state (not a cached URL), and it inherits the same stale-response guard
  // in applyUrl(). The inline script's own handler is now conditional on
  // this form NOT existing (see main-collection-product-grid.liquid) so the
  // two never double-bind on the same select.
  var sortSelect = document.getElementById('SortBy');
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      applyUrl(buildUrlFromForm());
    });
  }

  // ---------- price range / keyboard submit / mobile Apply button ----------

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    applyUrl(buildUrlFromForm());
    if (body.classList.contains('collection-filters-open')) closeDrawer();
  });

  // ---------- chips, clear-all, pagination — all real <a href> links ----------

  wrapper.addEventListener('click', function (e) {
    var chip = e.target.closest('.active-filters__chip, .active-filters__clear-all');
    if (chip) {
      e.preventDefault();
      applyUrl(chip.getAttribute('href'));
      return;
    }

    var pageLink = e.target.closest('.pagination__link[href]');
    if (pageLink) {
      e.preventDefault();
      applyUrl(pageLink.getAttribute('href'), { moveFocusToTop: true });
    }
  });

  // The panel's own "Clear all" link (footer of the drawer/sidebar, not the
  // chip row) uses the same real-<a> + intercept pattern.
  form.addEventListener('click', function (e) {
    var reset = e.target.closest('.collection-filters__reset');
    if (!reset) return;
    e.preventDefault();
    applyUrl(reset.getAttribute('href'));
  });

  // Browser back/forward after a pushState'd filter change. skipPushState:
  // the URL already changed natively, so re-pushing it would break the
  // back button (every popstate would shove a fresh, redundant entry back
  // onto the stack).
  window.addEventListener('popstate', function () {
    applyUrl(window.location.href, { skipPushState: true });
  });
});
